// Reads and writes of the finite schedule and the setup matrix (spec 03 §5
// rows 54 to 66, P3-I-2; rows 66 to 71, P3-I-3's moves, drag and pins), all
// through the generated client. Every queryFn
// passes its signal and every write carries a fresh Idempotency-Key. The board
// and the overloads read every page of their window, so a screen never shows
// half a lane.

import { useCallback } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap, unwrapVoid } from "../../../../lib/request";

type S = components["schemas"];

// The generated client drops `null` from a nullable reference to a named
// component: `openapi/v1.json` writes `pending_request` and `auto_publish` as
// a bare `$ref` and `schedule` as an `allOf` with an empty object, while the
// contract's zod and the wire both say null (the demand pool's
// `pending_request` has had the same shape since P3-G). These types put the
// null back, so a screen that checks for it is checking something real.
export type ScheduleAutoPublish = NonNullable<S["ScheduleAutoPublish"]>;
export type ScheduleHeader = Omit<S["ScheduleHeader"], "auto_publish"> & {
  auto_publish: ScheduleAutoPublish | null;
};
export type ScheduleOperation = S["ScheduleOperation"];
export type ScheduleBoard = Omit<S["ScheduleBoard"], "schedule" | "waiting_draft"> & {
  schedule: ScheduleHeader | null;
  waiting_draft: ScheduleHeader | null;
};
export type ScheduleLane = S["ScheduleLane"];
export type ScheduleLanePiece = S["ScheduleLanePiece"];
export type ScheduleDiff = Omit<S["ScheduleDiff"], "auto_publish"> & {
  auto_publish: ScheduleAutoPublish | null;
};
export type ScheduleMovedOperation = S["ScheduleMovedOperation"];
export type ScheduleOverloadRef = S["ScheduleOverloadRef"];
export type ScheduleOrderLabel = S["ScheduleOrderLabel"];
export type ScheduleRun = S["ScheduleRun"];
export type ScheduleSolveRequestState = S["ScheduleSolveRequestState"];
export type ScheduleRunList = Omit<S["ScheduleRunListResponse"], "pending_request"> & {
  pending_request: ScheduleSolveRequestState | null;
};
export type ScheduleSolveAccepted = S["ScheduleSolveAccepted"];
export type ScheduleOverload = S["ScheduleOverload"];
export type ScheduleMove = S["ScheduleMove"];
export type ScheduleMoveKind = ScheduleMove["kind"];
export type ScheduleMoveEffect = S["ScheduleMoveEffect"];
export type ScheduleMovesStatus = NonNullable<ScheduleOverload["moves_status"]>;
export type ScheduleUnpricedReason = NonNullable<ScheduleMove["unpriced_reason"]>;
export type ScheduleMovePreview = S["ScheduleMovePreview"];
export type ScheduleLateOrderChange = S["ScheduleLateOrderChange"];
export type ScheduleKpiDelta = S["ScheduleKpiDelta"];
export type ScheduleDirective = S["ScheduleDirective"];
export type ScheduleDirectiveKind = ScheduleDirective["kind"];
export type ScheduleMoveRequest = S["ScheduleMovePreviewRequest"];
export type CapacityLoad = S["CapacityLoad"];
export type CapacityLoadRow = S["CapacityLoadRow"];
export type SetupMatrix = S["SetupMatrix"];
export type SetupMatrixRow = S["SetupMatrixRow"];
export type SetupMatrixExplanation = S["SetupMatrixExplanation"];
export type SetupMatrixPutRequest = S["SetupMatrixPutRequest"];
export type ScheduleKpis = S["ScheduleKpis"];
export type AutoPublishHeldBy = ScheduleAutoPublish["held_by"][number];
export type ScheduleUnplacedReason = NonNullable<ScheduleOperation["unplaced_reason"]>;
export type ScheduleRunError = NonNullable<ScheduleRun["error"]>;
export type ScheduleRunTrigger = ScheduleRun["trigger"];
export type ScheduleRunStatus = ScheduleRun["status"];

export const UNPLACED_REASONS: readonly ScheduleUnplacedReason[] = [
  "work_center_uncalendared",
  "work_center_blocked",
  "no_capacity",
  "on_hold",
  "predecessor_unplaced",
  "no_routing",
];
export const RUN_ERRORS: readonly ScheduleRunError[] = [
  "LEASE_EXPIRED",
  "SHUTDOWN",
  "TOO_MANY_OPERATIONS",
  "COMPUTE_FAILED",
];
export const HELD_BY: readonly AutoPublishHeldBy[] = [
  "first_schedule",
  "auto_publish_off",
  "ops_moved",
  "new_overloads",
  "pins_released",
];
export const MOVE_KINDS: readonly ScheduleMoveKind[] = [
  "overtime",
  "alternate_work_center",
  "push_order",
];
export const UNPRICED_REASONS: readonly ScheduleUnpricedReason[] = [
  "currency_mismatch",
  "no_rate",
  "no_standard_cost",
];
export const DIRECTIVE_KINDS: readonly ScheduleDirectiveKind[] = [
  "pin_position",
  "pin_work_center",
  "defer",
];
export const RUN_TRIGGERS: readonly ScheduleRunTrigger[] = [
  "cron",
  "event",
  "manual",
  "drag_preview",
  "commit_move",
  "apply_move",
];

/** A page of the board or the overloads (contracts `PageLimit`). */
const PAGE = 200;
/** The most pages one board read follows: 5 000 operations, the solve's own cap (D-398 ruling 7). */
const MAX_PAGES = 26;
/** How often a read re-asks while a solve runs or waits. */
export const SCHEDULE_BUSY_POLL_MS = 5_000;
/** How often the board and the runs re-ask otherwise, so an event solve shows unasked. */
export const SCHEDULE_IDLE_POLL_MS = 60_000;

export const scheduleKeys = {
  all: (facilityId: string) => ["planning", "schedule", facilityId] as const,
  runs: (facilityId: string, cursor: string | undefined) =>
    ["planning", "schedule", facilityId, "runs", cursor ?? null] as const,
  run: (id: string) => ["planning", "schedule-run", id] as const,
  headers: (facilityId: string) => ["planning", "schedule", facilityId, "headers"] as const,
  board: (facilityId: string, version: string, from: string, to: string) =>
    ["planning", "schedule", facilityId, "board", version, from, to] as const,
  diff: (facilityId: string, id: string) =>
    ["planning", "schedule", facilityId, "diff", id] as const,
  overloads: (facilityId: string, id: string) =>
    ["planning", "schedule", facilityId, "overloads", id] as const,
  directives: (facilityId: string) => ["planning", "schedule", facilityId, "directives"] as const,
  capacity: (
    facilityId: string,
    scheduleId: string,
    from: string,
    to: string,
    grain: "day" | "week",
  ) => ["planning", "schedule", facilityId, "capacity", scheduleId, from, to, grain] as const,
  matrix: (facilityId: string, workCenterId: string) =>
    ["planning", "setup-matrix", facilityId, workCenterId] as const,
  explain: (
    facilityId: string,
    workCenterId: string,
    from: string,
    to: string,
    staticMinutes: string,
  ) =>
    [
      "planning",
      "setup-matrix",
      facilityId,
      workCenterId,
      "explain",
      from,
      to,
      staticMinutes,
    ] as const,
};

// ---- runs ----------------------------------------------------------------------------

function runsBusy(list: ScheduleRunList | undefined): boolean {
  if (list === undefined) return false;
  return list.items.some((r) => r.status === "running") || list.pending_request !== null;
}

/**
 * One page of the facility's solves, with the armed request and the failure
 * count (row 57). It reads `solves_only=true` (D-461 ruling 11), so neither a
 * CTP probe nor a drag preview, which write runs of their own, is ever shown
 * as the facility's last solve on the workbench tile, the board or the
 * dispatch board.
 */
export function useScheduleRuns(facilityId: string, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: scheduleKeys.runs(facilityId, cursor),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      runsBusy(query.state.data) ? SCHEDULE_BUSY_POLL_MS : SCHEDULE_IDLE_POLL_MS,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleRunList> =>
      unwrap(
        await client.GET("/v1/schedule-runs", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              limit: 20,
              solves_only: "true",
              ...(cursor !== undefined ? { cursor } : {}),
            },
          },
        }),
        "schedule runs",
      ),
  });
}

/** One solve; re-read while it runs (row 58). */
export function useScheduleRun(id: string | undefined) {
  return useQuery({
    queryKey: scheduleKeys.run(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? SCHEDULE_BUSY_POLL_MS : false,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleRun> =>
      unwrap(
        await client.GET("/v1/schedule-runs/{id}", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "schedule run",
      ),
  });
}

// ---- headers ---------------------------------------------------------------------------

export interface ScheduleHeads {
  readonly published: ScheduleHeader | null;
  readonly draft: ScheduleHeader | null;
}

/**
 * The facility's published schedule and its waiting draft, newest of each
 * (row 59). `busy` re-asks every 5 seconds, for a screen waiting on a solve
 * to publish, as the dispatch board does after a priority drag.
 */
export function useScheduleHeads(facilityId: string, enabled = true, busy = false, poll = true) {
  return useQuery({
    queryKey: scheduleKeys.headers(facilityId),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    // `poll: false` for a reader that must not move after it renders (the
    // office home, design-system §5).
    refetchInterval: poll ? (busy ? SCHEDULE_BUSY_POLL_MS : SCHEDULE_IDLE_POLL_MS) : false,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleHeads> => {
      const [published, draft] = await Promise.all(
        (["published", "draft"] as const).map(async (status) =>
          unwrap(
            await client.GET("/v1/schedules", {
              signal,
              params: { query: { facility_id: facilityId, status, limit: 1 } },
            }),
            "schedules",
          ),
        ),
      );
      return { published: published?.items[0] ?? null, draft: draft?.items[0] ?? null };
    },
  });
}

// ---- the board -----------------------------------------------------------------------

/** What the board needs of the header poll. */
export interface ScheduleHeadsState {
  readonly data: ScheduleHeads | undefined;
  readonly error: unknown;
}

/**
 * The board's version: the published schedule's id and the waiting draft's,
 * or "unknown" when the header poll failed, so the board still reads once.
 */
export function boardVersion(heads: ScheduleHeadsState): string {
  if (heads.data === undefined) return "unknown";
  return `${heads.data.published?.id ?? "none"}|${heads.data.draft?.id ?? "none"}`;
}

/** What the board shows: the published schedule, the facility's first draft, or nothing yet. */
export type BoardRead =
  | { readonly mode: "live"; readonly board: ScheduleBoard }
  | { readonly mode: "first_draft"; readonly board: ScheduleBoard }
  | { readonly mode: "none"; readonly board: ScheduleBoard };

/**
 * Every page of one schedule's board. The first page comes from `first`;
 * every later page is read by the first page's schedule id, so a publish
 * between two pages never mixes two schedules on the board (D-411 ruling 5).
 * The lanes and the unplaced list come on the first page alone.
 */
async function allPages(
  first: ScheduleBoard,
  readById: (id: string, cursor: string) => Promise<ScheduleBoard>,
): Promise<ScheduleBoard> {
  const id = first.schedule?.id;
  let operations = [...first.operations];
  let next = first.next_cursor;
  for (let page = 1; id !== undefined && next !== null && page < MAX_PAGES; page++) {
    const more = await readById(id, next);
    operations = operations.concat(more.operations);
    next = more.next_cursor;
  }
  return { ...first, operations, next_cursor: next };
}

/**
 * The board over a window of facility dates (rows 60 and 61). The live read
 * answers `schedule: null` before the first publish; when a first draft waits
 * the board reads that draft instead, read-only until a person publishes it
 * (§4.6 States).
 */
export function useScheduleBoard(
  facilityId: string,
  window: { from: string; to: string },
  heads: ScheduleHeadsState,
) {
  return useQuery({
    // The board never polls its pages. The header poll (`useScheduleHeads`,
    // two one-row reads) says when the published schedule or the waiting
    // draft changed, and its ids are this key, so a change re-reads the
    // board once and a quiet minute costs nothing (D-412 ruling 14).
    queryKey: scheduleKeys.board(facilityId, boardVersion(heads), window.from, window.to),
    enabled: facilityId.length > 0 && (heads.data !== undefined || heads.error !== null),
    retry: shouldRetryRead,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: ReadContext): Promise<BoardRead> => {
      const byId = async (id: string, cursor: string | undefined): Promise<ScheduleBoard> =>
        unwrap(
          await client.GET("/v1/schedules/{id}", {
            signal,
            params: {
              path: { id },
              query: {
                from: window.from,
                to: window.to,
                limit: PAGE,
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "schedule",
        );
      const head: ScheduleBoard = unwrap(
        await client.GET("/v1/schedules/live", {
          signal,
          params: {
            query: { facility_id: facilityId, from: window.from, to: window.to, limit: PAGE },
          },
        }),
        "schedule",
      );
      if (head.schedule !== null) {
        return { mode: "live", board: await allPages(head, byId) };
      }
      const draft = head.waiting_draft;
      if (draft === null) return { mode: "none", board: head };
      const board = await allPages(await byId(draft.id, undefined), byId);
      return { mode: "first_draft", board };
    },
  });
}

/** A version against its base (row 62). */
export function useScheduleDiff(facilityId: string, id: string | undefined) {
  return useQuery({
    queryKey: scheduleKeys.diff(facilityId, id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleDiff> =>
      unwrap(
        await client.GET("/v1/schedules/{id}/diff", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "schedule diff",
      ),
  });
}

/**
 * Every open overload of a schedule (row 66). While any of them carries moves
 * the runner has not scored yet, the read asks again every 5 seconds, so the
 * panel's "Scoring" turns into the scored effects without a reload.
 */
export function useScheduleOverloads(facilityId: string, id: string | undefined) {
  return useQuery({
    queryKey: scheduleKeys.overloads(facilityId, id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((o) => o.moves_status === "pending")
        ? SCHEDULE_BUSY_POLL_MS
        : false,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleOverload[]> => {
      const out: ScheduleOverload[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const result = unwrap(
          await client.GET("/v1/schedules/{id}/overloads", {
            signal,
            params: {
              path: { id: id ?? "" },
              query: {
                status: "open",
                limit: PAGE,
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "schedule overloads",
        );
        out.push(...result.items);
        if (result.next_cursor === null) break;
        cursor = result.next_cursor;
      }
      return out;
    },
  });
}

/** The facility's active pins and defers (row 70), every page up to the board's own cap. */
export function useScheduleDirectives(facilityId: string, enabled = true) {
  return useQuery({
    queryKey: scheduleKeys.directives(facilityId),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleDirective[]> => {
      const out: ScheduleDirective[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const result = unwrap(
          await client.GET("/v1/schedule-directives", {
            signal,
            params: {
              query: {
                facility_id: facilityId,
                status: "active",
                limit: PAGE,
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "schedule directives",
        );
        out.push(...result.items);
        if (result.next_cursor === null) break;
        cursor = result.next_cursor;
      }
      return out;
    },
  });
}

// ---- capacity --------------------------------------------------------------------------

/** Capacity by interval for one schedule, the published one when `scheduleId` is "published" (row 65). */
export function useCapacityLoad(
  facilityId: string,
  scheduleId: string | undefined,
  window: { from: string; to: string },
  grain: "day" | "week",
  enabled = true,
) {
  return useQuery({
    queryKey: scheduleKeys.capacity(facilityId, scheduleId ?? "", window.from, window.to, grain),
    enabled: enabled && facilityId.length > 0 && scheduleId !== undefined,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<CapacityLoad> =>
      unwrap(
        await client.GET("/v1/capacity/load", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              from: window.from,
              to: window.to,
              grain,
              ...(scheduleId !== undefined && scheduleId !== "published"
                ? { schedule_id: scheduleId }
                : {}),
            },
          },
        }),
        "capacity load",
      ),
  });
}

// ---- the setup matrix ----------------------------------------------------------------

/** One work centre's matrix with the values its routings use (row 54). */
export function useSetupMatrix(facilityId: string, workCenterId: string | undefined) {
  return useQuery({
    queryKey: scheduleKeys.matrix(facilityId, workCenterId ?? ""),
    enabled: facilityId.length > 0 && workCenterId !== undefined,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<SetupMatrix> =>
      unwrap(
        await client.GET("/v1/setup-matrix", {
          signal,
          params: { query: { facility_id: facilityId, work_center_id: workCenterId ?? "" } },
        }),
        "setup matrix",
      ),
  });
}

/** The kernel's changeover between two keys over the stored matrix (row 54a, D-408 ruling 1). */
export function useChangeoverExplanation(
  facilityId: string,
  workCenterId: string | undefined,
  ask: { from: string; to: string; staticMinutes: string } | undefined,
) {
  return useQuery({
    queryKey: scheduleKeys.explain(
      facilityId,
      workCenterId ?? "",
      ask?.from ?? "",
      ask?.to ?? "",
      ask?.staticMinutes ?? "",
    ),
    enabled: facilityId.length > 0 && workCenterId !== undefined && ask !== undefined,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<SetupMatrixExplanation> =>
      unwrap(
        await client.GET("/v1/setup-matrix/explain", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              work_center_id: workCenterId ?? "",
              ...(ask !== undefined && ask.from.length > 0 ? { from_key: ask.from } : {}),
              ...(ask !== undefined && ask.to.length > 0 ? { to_key: ask.to } : {}),
              ...(ask !== undefined && ask.staticMinutes.length > 0
                ? { static_minutes: ask.staticMinutes }
                : {}),
            },
          },
        }),
        "changeover",
      ),
  });
}

// ---- writes ----------------------------------------------------------------------------

/** `POST /v1/schedules/solve` (row 56): a new run, or the running one with a request queued. */
export async function requestSolve(facilityId: string): Promise<ScheduleSolveAccepted> {
  return unwrap(
    await client.POST("/v1/schedules/solve", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { facility_id: facilityId },
    }),
    "solve",
  );
}

export async function publishSchedule(id: string): Promise<ScheduleHeader> {
  return unwrap(
    await client.POST("/v1/schedules/{id}/publish", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "publish schedule",
  );
}

export async function discardSchedule(id: string): Promise<ScheduleHeader> {
  return unwrap(
    await client.POST("/v1/schedules/{id}/discard", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "discard schedule",
  );
}

/** `POST /v1/schedules/{id}/overloads/{overloadId}/apply-move` (row 67): the new published header. */
export async function applyScheduleMove(
  scheduleId: string,
  overloadId: string,
  moveId: string,
): Promise<ScheduleHeader> {
  const result = unwrap(
    await client.POST("/v1/schedules/{id}/overloads/{overloadId}/apply-move", {
      params: {
        path: { id: scheduleId, overloadId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body: { move_id: moveId },
    }),
    "apply move",
  );
  return result.schedule;
}

/** `POST /v1/schedules/{id}/preview-move` (row 68): the drag's consequences, nothing saved. */
export async function previewScheduleMove(
  scheduleId: string,
  body: ScheduleMoveRequest,
): Promise<ScheduleMovePreview> {
  return unwrap(
    await client.POST("/v1/schedules/{id}/preview-move", {
      params: { path: { id: scheduleId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "preview move",
  );
}

/** `POST /v1/schedules/{id}/commit-move` (row 69): the preview's body and run, the new published header. */
export async function commitScheduleMove(
  scheduleId: string,
  body: ScheduleMoveRequest & { preview_run_id: string },
): Promise<ScheduleHeader> {
  const result = unwrap(
    await client.POST("/v1/schedules/{id}/commit-move", {
      params: { path: { id: scheduleId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "commit move",
  );
  return result.schedule;
}

/** `DELETE /v1/schedule-directives/{id}` (row 71): unpins, which arms the solve. */
export async function removeScheduleDirective(id: string): Promise<void> {
  unwrapVoid(
    await client.DELETE("/v1/schedule-directives/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
    }),
    "unpin",
  );
}

/** `PUT /v1/setup-matrix/{workCenterId}` (row 55): the whole work centre's rows. */
export async function putSetupMatrix(
  workCenterId: string,
  body: SetupMatrixPutRequest,
): Promise<SetupMatrix> {
  return unwrap(
    await client.PUT("/v1/setup-matrix/{workCenterId}", {
      params: { path: { workCenterId }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "setup matrix",
  );
}

/** Re-reads every schedule read of a facility after a write or a finished solve. */
export function useScheduleRefresh() {
  const qc = useQueryClient();
  return useCallback(
    async (facilityId: string) => {
      await refreshQuery(qc, scheduleKeys.all(facilityId));
    },
    [qc],
  );
}
